import ApiService from "./api-service";
import ApiError from "../util/ApiError";

const addonFragment = /* GraphQL */ `
  fragment AddonPerUnitFragment on AddonPerUnitUsage {
    id
    type
    overages
    totalUsed
    planLimit
    overLimits
    canAccess
    totalIncludedUsers
    totalPurchasedUsers
    totalUsersLimit
    isUnlimitedUsersLimit
    includedUsersByPlan {
      moduleId
      moduleName
      planName
      includedUsers
    }
    scheduled {
      overages
      overagesChange
      price
      scheduledAt
    }
    price
    name
  }

  fragment AddonPerUnitSharedFragment on TemplateAddonUsage {
    id
    type
    overages
    planLimit
    sharedLimits {
      email {
        totalUsed
        overLimits
        canAccess
      }
      sms {
        totalUsed
        overLimits
        canAccess
      }
      phoneCall {
        totalUsed
        overLimits
        canAccess
      }
      letter {
        totalUsed
        overLimits
        canAccess
      }
    }
    scheduled {
      overages
      overagesChange
      price
      scheduledAt
    }
    price
    name
  }

  fragment AddonFlatFeeFragment on AddonFlatFeeUsage {
    id
    type
    overages
    canAccess
    includedInPlan
    scheduled {
      overages
      overagesChange
      price
      scheduledAt
    }
    price
    name
  }

  fragment AddonVolumeFragment on AddonVolumeUsage {
    type
    id
    overages
    canAccess
    totalUsed
    planLimit
    scheduled {
      overages
      overagesChange
      price
      scheduledAt
    }
    price
    name
    proratedOverages
  }
`;

const SettingsService = {
  async getUsers(ShortCode) {
    const body = {
      query: `query {
        organisation {
          ShortCode
          createdBy {
            _id
            profile {
              firstName
              lastName
            }
            emails {
              address
            }
          }
          chaserUsers {
            _id
            profile {
              firstName
              lastName
            }
            activity {
              type
              date
            }
            permissions {
              global
              organisation {
                roles
                orgShortCode
              }
            }
            emails {
              address
            }
          }
          invitedChaserUsers {
            email
            roles
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation;
  },
  async getUserActivity({ ShortCode, from, to }) {
    const body = {
      query: `query($from: Date, $to: Date) {
        organisation {
          ShortCode
          chaserUsers {
            _id
            profile {
              firstName
              lastName
            }
            activity(from: $from, to: $to) {
              type
              date
            }
          }
        }
      }
      `,
      variables: {
        from,
        to,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation.chaserUsers;
  },
  async inviteUser(ShortCode, email, roles, resendInvite) {
    const body = {
      query: `mutation($email: NonEmptyString!, $roles: [String]!, $resendInvite: Boolean) {
        inviteChaserUser(email: $email, roles: $roles, resendInvite: $resendInvite) {
          invitedChaserUsers {
            email
            roles
          }
          chaserUsers {
            _id
            emails {
              address
            }
          }
        }
      }
    `,
      variables: { email: email.toLowerCase(), roles, resendInvite },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.inviteChaserUser.invitedChaserUsers;
  },
  async deleteUnverifiedUser(ShortCode, email) {
    const body = {
      query: `
      mutation($email: NonEmptyString!) {
        removeInvitedChaserUser(email: $email) {
          invitedChaserUsers {
            email
          }
        }
      }
    `,
      variables: { email },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.removeInvitedChaserUser.invitedChaserUsers;
  },
  async deleteUser(ShortCode, userId) {
    const body = {
      query: `mutation($userId: String!) {
        deleteUser(userId: $userId)
      }
    `,
      variables: { userId },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);

    if (!data.deleteUser) throw new ApiError("Failed to delete user");
    return true;
  },
  async getIntegration(ShortCode) {
    const body = {
      query: `query {
        organisation {
          accountingSoftware
          accessToken {
            expired
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation;
  },
  async disconnectAccountingSoftware(ShortCode) {
    const body = {
      query: `mutation {
        disconnectOrganisation
      }
      `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.disconnectOrganisation;
  },
  async getModules(ShortCode) {
    const body = {
      query: /* GraphQL */ `
        ${addonFragment}
        query {
          organisation {
            invoicesChasedThisMonth
            subscription {
              _id
              summary {
                status
                commitmentTerm
                billed
                nextBillDate
                nextChargeDate
                currency
                paymentMethod {
                  brand
                  last4
                  expiryMonth
                  expiryYear
                }
                totalAmountMinor
                totalAmount
                scheduledChanges {
                  key
                  id
                  addonKey
                  moduleId
                  name
                  changeDate
                  isRemoving
                  currentOverages
                  scheduledOverages
                  price
                }
                lineItems {
                  id
                  title
                  description
                  quantity
                  amountMinor
                  amount
                  children {
                    id
                    addonKey
                    title
                    description
                    quantity
                    amountMinor
                    amount
                  }
                }
              }
              plan {
                title
                name
                period
                amount
                isPlanOverLimits
                addons {
                  users {
                    ...AddonPerUnitFragment
                  }
                  templates {
                    ...AddonPerUnitSharedFragment
                  }
                  schedules {
                    ...AddonPerUnitFragment
                  }
                  emailIntegrationConnections {
                    ...AddonPerUnitFragment
                  }
                  receivablesForecasting {
                    ...AddonFlatFeeFragment
                  }
                  tfpEmails {
                    ...AddonFlatFeeFragment
                  }
                  creditReport {
                    ...AddonVolumeFragment
                  }
                  sms {
                    ...AddonVolumeFragment
                  }
                  phoneCall {
                    ...AddonVolumeFragment
                  }
                  letter {
                    ...AddonVolumeFragment
                  }
                }
                scheduledPlan {
                  id
                  name
                  amount
                  quantity
                  period
                  currency
                }
                scheduledDate
                nextBillingDate
              }
              billingCurrency
              subscriber {
                _id
                profile {
                  firstName
                  lastName
                }
              }
              bundle {
                name
              }
              pendingDowngrade {
                plan {
                  title
                }
                startDate
              }
              subscriptionService
              subscriptionId
              isInTrial
              isExpiredTrial
              isSubscribed
              isCancelled
              trialExpiryDate
              isSuspended
              isBundle
              moduleSubscriptions {
                id
                moduleName
                planName
                subscriptionId
                subscriber
                amountMinor
                period
                nextBillingDate
                status
                isInTrial
                trialEndsAt
                isActive
                supportsAddons
                features
                addons {
                  ... on OrganisationAddonFeatureUsage {
                    users {
                      ...AddonPerUnitFragment
                    }
                  }
                  ... on ReceivablesAddonFeatureUsage {
                    users {
                      ...AddonPerUnitFragment
                    }
                    templates {
                      ...AddonPerUnitSharedFragment
                    }
                    schedules {
                      ...AddonPerUnitFragment
                    }
                    emailIntegrationConnections {
                      ...AddonPerUnitFragment
                    }
                    receivablesForecasting {
                      ...AddonFlatFeeFragment
                    }
                    tfpEmails {
                      ...AddonFlatFeeFragment
                    }
                    creditReport {
                      ...AddonVolumeFragment
                    }
                    sms {
                      ...AddonVolumeFragment
                    }
                    phoneCall {
                      ...AddonVolumeFragment
                    }
                    letter {
                      ...AddonVolumeFragment
                    }
                  }
                  ... on CashflowAddonFeatureUsage {
                    users {
                      ...AddonPerUnitFragment
                    }
                  }
                }
              }
            }
          }
        }
      `,
    };
    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation;
  },
  async getBillingInfo({ ShortCode, moduleName }) {
    const body = {
      query: /* GraphQL */ `
        query ($moduleName: String) {
          organisation {
            partner {
              ShortCode
              subscriber {
                _id
                firstName
                lastName
              }
            }
            availablePlans(moduleName: $moduleName) {
              title
              name
              prices {
                currency
                amount
                period
              }
            }
          }
        }
      `,
      variables: {
        moduleName,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation;
  },
  async getActionsCompleted(ShortCode) {
    const body = {
      query: `query {
          organisation {
            actionsCompleted {
              editSignature
              editStatement
              setChaseTime
              reviewTemplates
              addSchedule
              startChasing
            }
          }
        }`,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation.actionsCompleted || {};
  },
  async setActionCompleted(ShortCode, action) {
    const body = {
      query: `mutation($action: String!) {
        setActionCompleted(action: $action)
      }`,
      variables: { action },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.setActionCompleted;
  },
  async setOrganisationName(organisationName, shortCode) {
    const body = {
      query: `mutation($organisationName: NonEmptyString!) {
        setOrganisationName(organisationName: $organisationName)
      }
    `,
      variables: {
        organisationName,
      },
    };

    await ApiService.orgEndpoint(shortCode).post("/", body);
    return true;
  },
  async setOrganisationTimezone(timezone, shortCode) {
    const body = {
      query: `mutation($timezone: NonEmptyString!) {
        setOrganisationTimezone(timezone: $timezone)
      }
    `,
      variables: {
        timezone,
      },
    };

    await ApiService.orgEndpoint(shortCode).post("/", body);
    return true;
  },
  async setOrganisationAddress({
    address,
    floor,
    postal,
    city,
    country,
    region,
    addressType,
    shortCode,
  }) {
    const body = {
      query: `mutation($address: NonEmptyString!, $floor: String, $postal: NonEmptyString!, $region: String, $city: String, $country: NonEmptyString!, $addressType: String) {
        setOrganisationAddress(address: $address, floor: $floor, postal: $postal, city: $city, region: $region, country: $country, addressType: $addressType)
      }
    `,
      variables: {
        address,
        floor,
        postal,
        city,
        country,
        region,
        addressType,
      },
    };

    await ApiService.orgEndpoint(shortCode).post("/", body);
    return true;
  },
  async getIntegrationFeatures(shortCode) {
    const body = {
      query: /* GraphQL */ `
        query {
          organisation {
            features {
              custom_invoice_pdf
              hide_intercompany_docs
              customer_recipients_visible_labels
              collections_provider
            }
          }
        }
      `,
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.organisation.features;
  },
  async setCustomInvoicePdf(shortCode, enabled) {
    const body = {
      query: /* GraphQL */ `
        mutation ($enabled: Boolean!) {
          setCustomInvoicePdf(enabled: $enabled)
        }
      `,
      variables: {
        enabled,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.setCustomInvoicePdf;
  },
  async setHideIntercompanyDocs(shortCode, enabled) {
    const body = {
      query: /* GraphQL */ `
        mutation ($enabled: Boolean!) {
          setHideIntercompanyDocs(enabled: $enabled)
        }
      `,
      variables: {
        enabled,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.setHideIntercompanyDocs;
  },
  async setCustomerRecipientsVisibleLabels(shortCode, labels) {
    const body = {
      query: /* GraphQL */ `
        mutation ($labels: [String!]!) {
          setCustomerRecipientsVisibleLabels(labels: $labels)
        }
      `,
      variables: {
        labels,
      },
    };

    await ApiService.orgEndpoint(shortCode).post("/", body);
    return true;
  },
  async setSubsidiaries(shortCode, subsidiaries) {
    const body = {
      query: /* GraphQL */ `
        mutation ($subsidiaries: [String!]!) {
          setSubsidiaries(subsidiaries: $subsidiaries)
        }
      `,
      variables: {
        subsidiaries,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.setSubsidiaries;
  },

  async requestReceivablesForecastAddon(ShortCode) {
    const body = {
      query: `mutation { requestReceivablesForecastAddon }`,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.requestReceivablesForecastAddon;
  },

  async startCashFlowForecastTrial(ShortCode) {
    // First fetch subscription to get billing currency
    const subscriptionData = await this.getSubscription({ ShortCode });
    const billingCurrency =
      subscriptionData?.subscription?.billingCurrency || "GBP";

    // Call startModuleSubscriptionTrial with cash flow module parameters
    const body = {
      query: /* GraphQL */ `
        mutation (
          $moduleName: String!
          $planName: String!
          $currency: String!
          $period: String
        ) {
          startModuleSubscriptionTrial(
            moduleName: $moduleName
            planName: $planName
            currency: $currency
            period: $period
          )
        }
      `,
      variables: {
        moduleName: "cashflow-module",
        planName: "cashflow-trial",
        currency: billingCurrency,
        period: "month",
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.startModuleSubscriptionTrial;
  },

  async requestCashFlowForecastAddon(ShortCode) {
    const body = {
      query: `mutation { requestReceivablesForecastAddon }`,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.requestReceivablesForecastAddon;
  },
};

export default SettingsService;
